import type { SxProps, Theme } from '@mui/material/styles';
import MuiTooltip, { type TooltipProps } from '@mui/material/Tooltip';

type TooltipVariant = 'dark' | 'light';

type Props = TooltipProps & {
  variant?: TooltipVariant;
};

type TooltipSlotProps = NonNullable<TooltipProps['slotProps']>['tooltip'];
type TooltipSlotPropsObject = Exclude<NonNullable<TooltipSlotProps>, (...args: never[]) => unknown>;

const lightTooltipSx: SxProps<Theme> = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[2],
  color: theme.palette.text.primary,
});

const toSxArray = (sx: SxProps<Theme> | undefined) => (Array.isArray(sx) ? sx : sx ? [sx] : []);

const getLightSlotProps = (slotProps: TooltipProps['slotProps']): TooltipProps['slotProps'] => {
  const tooltipSlotProps = slotProps?.tooltip;

  if (typeof tooltipSlotProps === 'function') {
    return {
      ...slotProps,
      tooltip: (ownerState) => {
        const resolvedTooltipSlotProps = tooltipSlotProps(ownerState);

        return {
          ...resolvedTooltipSlotProps,
          sx: [lightTooltipSx, ...toSxArray(resolvedTooltipSlotProps.sx as SxProps<Theme> | undefined)],
        };
      },
    };
  }

  return {
    ...slotProps,
    tooltip: {
      ...tooltipSlotProps,
      sx: [
        lightTooltipSx,
        ...toSxArray((tooltipSlotProps as TooltipSlotPropsObject | undefined)?.sx as SxProps<Theme> | undefined),
      ],
    },
  };
};

export const Tooltip = ({ children, slotProps, variant = 'dark', ...props }: Props) => {
  const resolvedSlotProps = variant === 'light' ? getLightSlotProps(slotProps) : slotProps;

  return (
    <MuiTooltip {...props} enterTouchDelay={0} slotProps={resolvedSlotProps}>
      {children}
    </MuiTooltip>
  );
};

Tooltip.displayName = 'Tooltip';
